import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { _DOCFileRecord, _single } from "../../../../../core/services/dataBean/EntityCatalog";
import { SelectTrdsID } from "../../../../admin/configuracion/TrdID/SelectTrdsID";
import { BasicButton } from "../../../../theme/Component/BasicButton";
import { Modal } from "../../../../theme/Component/Modal";
import useModal, { MODAL_md } from "../../../../theme/Component/hooks/useModal";
import usePaginator from "../../../../theme/Component/usePaginator";
import type { DocumentFolderItem } from "../DocumentFileManager.types";
import { mapFileRecordToFolderItem } from "../DocumentFileManager.types";
import { PrintJson } from "../../../../theme/Component/PrintJson";

interface DocumentRecordsViewProps {
    onSelectFolder: (folder: DocumentFolderItem) => void;
}

export interface DocumentRecordFormValue {
    name: string;
    description: string;
    idln: number | null;
}

interface DocumentRecordFormFieldsProps {
    value: DocumentRecordFormValue;
    onChange: (value: DocumentRecordFormValue) => void;
}

export const DocumentRecordFormFields = ({ value, onChange }: DocumentRecordFormFieldsProps) => (
    <div className="row">
        <div className="col-md-12 mb-3">
            <label>Nombre <span className="text-danger">*</span></label>
            <input
                className="form-control"
                type="text"
                value={value.name}
                onChange={(event) => onChange({ ...value, name: event.target.value })}
            />
        </div>
        <div className="col-md-12 mb-3">
            <label>Descripcion</label>
            <textarea
                className="form-control"
                value={value.description}
                onChange={(event) => onChange({ ...value, description: event.target.value })}
            />
        </div>
        <div className="col-md-12">
            <SelectTrdsID
                edit={false}
                onChange={(trd: any) => onChange({ ...value, idln: trd.IDLn })}
            />
        </div>
    </div>
);

export const DocumentRecordsView = ({ onSelectFolder }: DocumentRecordsViewProps) => {
    const { instance: instanceRecord, setLista: setListaRecord } = useDataBean(_DOCFileRecord);
    const paginadorRecord = usePaginator();
    const modalCreateRecord = useModal("Crear expediente", MODAL_md);
    const [folders, setFolders] = useState<DocumentFolderItem[]>([]);
    const [recordForm, setRecordForm] = useState({
        name: "",
        description: "",
        idln: null as number | null,
    });

    useEffect(() => {
        getFileRecord();
    }, [paginadorRecord.page]);

    const getFileRecord = () => {
        instanceRecord.getDOCFileRecordRender(null, null, { IDEmployee: _single.getAccountID() }).then(
            (resp) => {
                paginadorRecord.setPages(resp.NumberOfPages ?? 1);
                paginadorRecord.setRecords((resp.NumberOfPages ?? 1) * 15);
                paginadorRecord.setRegiPerPage(15);

                setListaRecord(resp.List ?? []);
                setFolders((resp.List ?? []).map(mapFileRecordToFolderItem));
            }
        ).catch(err => { toast.error(err) });
    };

    const openCreateRecord = () => {
        setRecordForm({ name: "", description: "", idln: null });
        modalCreateRecord.open();
    };

    const submitCreateRecord = () => {
        if (!recordForm.name.trim()) {
            toast.error("Debe ingresar el nombre");
            return;
        }

        if (!recordForm.idln) {
            toast.error("Debe seleccionar un TRD");
            return;
        }

        instanceRecord.createDOCFileRecord(
            _single.getAccountID(),
            recordForm.name.trim(),
            recordForm.description.trim(),
            recordForm.idln, null, null
        ).then(
            () => {
                getFileRecord();
                modalCreateRecord.close();
            }
        ).catch(err => {
            toast.error(err);
        });
    };

    return (
        <>
            <section className="document-records-view">
                <div className="document-records-view__header">
                    <div>
                        <span className="document-records-view__eyebrow">Repositorio</span>
                        <h1 className="document-records-view__title">Expedientes</h1>
                    </div>

                    <BasicButton icon="ri-add-line" eventClick={openCreateRecord} clase="primary" timeStopClickSeg={250}>
                        Crear expediente
                    </BasicButton>
                </div>

                <div className="document-records-view__list" aria-label="Listado de expedientes">
                    {folders.map((folder) => (
                        <button
                            type="button"
                            key={folder.id}
                            className="document-records-view__item"
                            onClick={() => onSelectFolder(folder)}
                            title={folder.path || folder.name}
                        >
                            <span className="document-records-view__icon">
                                <i className="ri-folder-line"></i>
                            </span>

                            <span className="document-records-view__info">
                                <span className="document-records-view__name">{folder.name}</span>
                                <span className="document-records-view__meta">
                                    {[folder.trdName, folder.recentDate].filter(Boolean).join(" - ")}
                                </span>
                            </span>

                            <span className="document-records-view__open">
                                <i className="ri-arrow-right-s-line"></i>
                            </span>
                        </button>
                    ))}

                    {folders.length === 0 &&
                        <div className="document-records-view__empty">
                            No hay expedientes para mostrar.
                        </div>
                    }
                </div>
            </section>

            <Modal modal={modalCreateRecord.modal} updateModal={modalCreateRecord.setModal} eventModal={submitCreateRecord} onSubmitLabel="Crear">
                <DocumentRecordFormFields value={recordForm} onChange={setRecordForm} />
            </Modal>
        </>
    );
};
